<script setup lang="ts">
import "./MXmlJsonViewer.scss";
import { computed, onBeforeUnmount, ref, withDefaults } from "vue";
import MBadge from "../MBadge";
import type { MXmlJsonViewerProps } from "./MXmlJsonViewer.props";
import { mXmlJsonViewerDefaults } from "./MXmlJsonViewer.props";
import { detectFormat, formatContent } from "./MXmlJsonViewer.utils";

const props = withDefaults(defineProps<MXmlJsonViewerProps>(), mXmlJsonViewerDefaults);

const format = computed(() => detectFormat(props.content));
const formatted = computed(() => formatContent(props.content, format.value));
const badgeLabel = computed(() => format.value.toUpperCase());

const copied = ref(false);
let resetTimer: ReturnType<typeof setTimeout> | undefined;

async function copy() {
  try {
    await navigator.clipboard.writeText(formatted.value);
    copied.value = true;
    clearTimeout(resetTimer);
    resetTimer = setTimeout(() => (copied.value = false), 1500);
  } catch {
    // clipboard unavailable / denied — leave the button in its idle state
  }
}

onBeforeUnmount(() => clearTimeout(resetTimer));
</script>

<template>
  <div class="m-xml-json-viewer" :class="{ 'm-xml-json-viewer--wrap': wrap }">
    <div class="m-xml-json-viewer__toolbar">
      <m-badge class="m-xml-json-viewer__badge" :navy="format === 'xml'" :green="format === 'json'">
        <svg
          v-if="showFormatIcon"
          class="m-xml-json-viewer__badge-icon"
          viewBox="0 0 24 24"
          width="12"
          height="12"
          aria-hidden="true"
        >
          <path
            fill="currentColor"
            d="M9.4 16.6 4.8 12l4.6-4.6L8 6l-6 6 6 6zm5.2 0 4.6-4.6-4.6-4.6L16 6l6 6-6 6z"
          />
        </svg>
        {{ badgeLabel }}
      </m-badge>
      <button
        v-if="copyable"
        type="button"
        class="m-xml-json-viewer__copy"
        :class="{ 'm-xml-json-viewer__copy--copied': copied }"
        :aria-label="copied ? 'Copied' : 'Copy to clipboard'"
        @click.stop="copy"
      >
        <svg
          v-if="copied"
          class="m-xml-json-viewer__copy-icon"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          aria-hidden="true"
        >
          <path fill="currentColor" d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
        </svg>
        <svg
          v-else
          class="m-xml-json-viewer__copy-icon"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          aria-hidden="true"
        >
          <path
            fill="currentColor"
            d="M16 1H4a2 2 0 0 0-2 2v14h2V3h12zm3 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2m0 16H8V7h11z"
          />
        </svg>
      </button>
    </div>
    <!-- tabindex so keyboard users can scroll the region when content overflows (WCAG 2.1.1) -->
    <pre class="m-xml-json-viewer__content" tabindex="0">{{ formatted }}</pre>
  </div>
</template>
